"use client";
import { BoolToggle } from "@app-foundation/ui-controls";
import "@app-foundation/ui-controls/styles.css";
import { LICENSE_PACKAGES } from "@/features/platform-license/contract";
import { isInternalTestAvatarId } from "@/features/plaza/internal-test-avatar";
import { ScrollConsent, type PrivacyConsentHandle } from "@/features/service-information/PrivacyConsent";
import { agreement, agreementTerm } from "../agreement-document";
import { prepareApplicationScope } from "../application-scope";
import { authorizationScopeError, parseAuthorizationScope, type AuthorizationScope } from "../authorization-scope";
import { AuthorizationScopeFields } from "./AuthorizationScopeFields";
import type { AgreementChoices } from "../agreement-choices";
import { ProjectAgreementChoices } from "./ProjectAgreementChoices";
import { GuidedSubmitButton } from "@/components/forms/GuidedSubmitButton";

import { createContext, useContext, useEffect, useId, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "@app-foundation/icons/lucide";
import { Button, ButtonLink } from "@/components/ui/Button";
import { H5PaymentPanel, isH5PaymentResult, rememberH5Payment, type H5PaymentResult } from "./H5PaymentPanel";
import { parseAuthorizationOffer, type AuthorizationOffer } from "../offer";
import { legacyAuthorizationAttemptKey, packageAttemptKey, parsePackageAttempt, startNewProjectAttempt, type PackageAttempt, type PackageApplicationFields } from "../package-attempt";
import { NativePaymentPanel } from "./NativePaymentPanel";
import { parseProjectLegacyAttempt, type ProjectLegacyAttempt, type ProjectApplicationContext } from "@/features/projects/application-context";

type ApplicationResult = Readonly<{
  authorizationId: string;
  amount: number;
  paid: boolean;
  qrCodeDataUrl: string | null;
}>;

const subscribeToHydration = () => () => undefined;
const getClientSnapshot = () => true;
const getServerSnapshot = () => false;
const getPaymentBrowser = () => /MicroMessenger/iu.test(navigator.userAgent) ? "wechat" : /Android|iPhone|iPad|iPod/iu.test(navigator.userAgent) ? "h5" : "native";
const serverPaymentBrowser = () => "native";

type FormProps = { buyer?: { companyName: string; creditCodeLast4?: string | null }; freeViewingEnabled?: boolean; demo?: boolean; autoOpen?: boolean; projectContext?: ProjectApplicationContext; avatarId: string; h5Enabled?: boolean; userId?: number; packageEnabled?: boolean; repeatProjectAvailable?: boolean };

const ApplicationStepSelect = createContext<((step: number) => void) | null>(null);
const ApplicationStepChange = createContext<((step: number) => void) | null>(null);
const ApplicationPreview = createContext<{ step: number; onChange: (step: number) => void } | null>(null);
const ApplicationStepsContainer = createContext<HTMLDivElement | null>(null);

export function WebAuthorizationApplicationForm(props: FormProps) {
  const [open, setOpen] = useState(props.autoOpen === true);
  const [opened, setOpened] = useState(props.autoOpen === true);
  const [step, setStep] = useState(1);
  const [viewing, setViewing] = useState(true);
  const [viewIndex, setViewIndex] = useState<number | null>(props.freeViewingEnabled ? 1 : null);
  const selecting = props.freeViewingEnabled && viewing ? setViewIndex : null;
  const previewing = Boolean(selecting && viewIndex !== null);
  const [stepsContainer, setStepsContainer] = useState<HTMLDivElement | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (open) dialog.current?.showModal();
    else dialog.current?.close();
    if (!open) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; };
  }, [open]);
  return <div className="mt-5">
    <div className="flex justify-center"><Button type="button" className="w-full" onClick={() => { setOpened(true); setOpen(true); }}>{props.repeatProjectAvailable ? "开始新项目申请" : "申请授权"}</Button></div>
    <dialog ref={dialog} aria-labelledby={titleId} onCancel={() => setOpen(false)} onClose={() => setOpen(false)} className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-[560px] overflow-y-auto rounded-2xl border border-[var(--color-line)] bg-[var(--color-raised)] p-5 text-[var(--color-paper)] shadow-2xl backdrop:bg-black/60 sm:p-6">
      <h2 id={titleId} className="sr-only">{step === 3 ? "订单支付" : step === 4 ? "授权审核" : "申请授权"}</h2>
      {props.freeViewingEnabled && <div className="mb-4 flex justify-center text-sm"><BoolToggle label="自由查看" value={viewing} onChange={value => { setViewing(value); setViewIndex(value ? 1 : null); }} /></div>}
      <div className="relative">
        <div ref={setStepsContainer} className="pr-11" />
        <button type="button" aria-label="关闭授权申请" className="absolute right-0 top-0 inline-flex min-h-11 min-w-11 items-center justify-center" onClick={() => setOpen(false)}><X size={20} aria-hidden="true" /></button>
      </div>
      <ApplicationStepSelect.Provider value={selecting}>
        <ApplicationPreview.Provider value={previewing ? { step: viewIndex!, onChange: setViewIndex } : null}>
          <ApplicationStepChange.Provider value={setStep}><ApplicationStepsContainer.Provider value={stepsContainer}>
            {opened && <RepeatProjectGate key={`${props.userId}:${props.avatarId}:${props.packageEnabled}:${props.repeatProjectAvailable}:${props.projectContext?.projectId}:${props.projectContext?.roleId}`} {...props} />}
          </ApplicationStepsContainer.Provider></ApplicationStepChange.Provider>
        </ApplicationPreview.Provider>
      </ApplicationStepSelect.Provider>
    </dialog>
  </div>;
}

function ApplicationSteps({ step }: { step: number }) {
  const container = useContext(ApplicationStepsContainer);
  const select = useContext(ApplicationStepSelect);
  const onStepChange = useContext(ApplicationStepChange);
  useEffect(() => { onStepChange?.(step); }, [onStepChange, step]);
  if (!container) return null;
  return createPortal(<ol aria-label="授权申请进度" className="mb-4 grid grid-cols-3 gap-2 border-b border-[var(--color-line)] text-center text-xs">{[{ label: "申请授权", step: 1 }, { label: "订单支付", step: 3 }, { label: "授权审核", step: 4 }].map(item => <li key={item.step} aria-current={item.step === step ? "step" : undefined} className={`border-b-2 py-3 ${item.step === step ? "border-[var(--color-accent)] text-[var(--color-accent)]" : "border-transparent text-[var(--color-muted)]"}`}>{select ? <button type="button" className="w-full" onClick={() => select(item.step)}>{item.label}</button> : item.label}</li>)}</ol>, container);
}

function RepeatProjectGate(props: FormProps) {
  const [started, setStarted] = useState(!props.repeatProjectAvailable);
  const [error, setError] = useState("");
  if (!started) return <div className="mt-5 grid gap-3 text-sm">
    <p>原订单会保留。请确认这是另一个项目，再开始新的授权申请。</p>
    {error && <p role="alert" className="text-[var(--color-pending)]">{error}</p>}
    <Button type="button" onClick={() => {
      try {
        if (!props.projectContext) startNewProjectAttempt(sessionStorage, props.userId ?? 0, props.avatarId);
        setError(""); setStarted(true);
      } catch { setError("浏览器无法清除原付款请求，请检查存储权限后重试。"); }
    }}>{props.projectContext ? "继续此角色的授权申请" : "开始新项目申请"}</Button>
    <ButtonLink href="/partner/authorizations" variant="secondary">查看原订单</ButtonLink>
  </div>;
  return props.demo ? <ApplicationForm {...props} demo /> : <PackageForm {...props} />;
}

function PackageForm(props: FormProps) {
  const [offer, setOffer] = useState<AuthorizationOffer | null>(null);
  const radioName = useId();
  const [offers, setOffers] = useState<AuthorizationOffer[]>([]);
  const [legacy, setLegacy] = useState(false);
  const [saved, setSaved] = useState<PackageAttempt | null>(null);
  const [draft, setDraft] = useState<PackageApplicationFields | undefined>();
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        let raw: string | null = null;
        try { raw = sessionStorage.getItem(packageAttemptKey(props.userId ?? 0, props.avatarId, props.projectContext)); }
        catch { if (props.packageEnabled) throw new Error("浏览器无法保留付款请求，请检查存储权限后重试。"); }
        if (raw) {
          const pending = parsePackageAttempt(JSON.parse(raw), props.avatarId);
          if (!pending) throw new Error("之前的付款请求无法恢复，请先到授权中心核对原单。");
          setSaved(pending); setOffer(pending.offer); return;
        }
        const legacyRaw = sessionStorage.getItem(legacyAuthorizationAttemptKey(props.userId ?? 0, props.avatarId, props.projectContext));
        if (legacyRaw) {
          if (!parseProjectLegacyAttempt(JSON.parse(legacyRaw), props.avatarId)) throw new Error("之前的付款请求无法完整恢复，请先到授权中心核对原单。");
          // A changed checkout feature flag must not discard an unresolved
          // legacy request and create a second order through package checkout.
          setLegacy(true); return;
        }
        if (!props.packageEnabled) throw new Error("新版授权条款暂未开放，请稍后再试。");
        const response = await fetch("/api/v1/cloudbase-authorizations/offer", { method: "POST", cache: "no-store", signal: controller.signal,
          headers: { "Content-Type": "application/json" }, body: JSON.stringify({ avatarId: props.avatarId }) });
        const body = await response.json();
        if (response.ok && body.enabled === false) throw new Error("新版授权条款暂未开放，请稍后再试。");
        const current = body.enabled === true ? parseAuthorizationOffer(body.offer) : null;
        if (!response.ok || !current || current.version !== 3 || current.avatarId !== props.avatarId) throw new Error("授权条款暂时不可用，请稍后重试。");
        const choices = Array.isArray(body.offers) ? body.offers.map((item: unknown) => parseAuthorizationOffer(item)) : [current];
        if ((choices.length < 1 || choices.length > 3) || choices.some((item: AuthorizationOffer | null) => !item || item.version !== 3 || item.avatarId !== props.avatarId)) throw new Error("授权条款列表无效，请重新读取。");
        if (!controller.signal.aborted) { setOffer(current); setOffers(choices as AuthorizationOffer[]); }
      } catch (caught) {
        if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : "无法读取授权条款，请稍后重试。");
      }
    })();
    return () => controller.abort();
  }, [props.avatarId, props.userId, props.packageEnabled, props.projectContext, revision]);
  const reload = () => { setOffer(null); setSaved(null); setError(""); setRevision(value => value + 1); };
  if (legacy) return <ApplicationForm {...props} />;
  if (!offer) return <div className="mt-5 grid gap-3 text-sm">
    <p role={error ? "alert" : "status"}>{error || "正在读取当前授权条款…"}</p>
    {error && <Button type="button" variant="secondary" onClick={reload}>重新读取授权条款</Button>}
    <ButtonLink href="/partner/authorizations" variant="secondary">查看原订单</ButtonLink>
  </div>;
  return <>{notice && <p role="status" className="mt-4 text-sm">{notice}</p>}<ApplicationForm key={offer.quoteHash} {...props} offer={offer} initialPackageAttempt={saved} initialFields={draft} onFieldsChange={setDraft} onAttemptSaved={setSaved}
    authorizationChoices={!saved && offers.length > 1 ? <fieldset className="grid gap-2"><legend className="mb-2 text-sm font-medium">授权用途</legend>{offers.map(choice => <label key={choice.quoteHash} className="flex cursor-pointer items-center gap-3 rounded-xl border border-[var(--color-line)] p-3 text-sm"><input type="radio" name={radioName} checked={offer.quoteHash === choice.quoteHash} onChange={() => setOffer(choice)} /><span className="flex-1">{choice.package.name}<small className="ml-2 text-[var(--color-muted)]">{choice.package.platformPackage?.summary}</small></span><b>¥{(choice.amountMinor / 100).toFixed(2)}</b></label>)}</fieldset> : null}
    onOfferRejected={fields => { setDraft(fields); setNotice("请重新查看并确认当前报价后提交。"); reload(); }} /></>;
}

function ApplicationForm({ demo = false, buyer, projectContext, avatarId, h5Enabled = false, userId = 0, offer, initialPackageAttempt = null, initialFields, onOfferRejected, onFieldsChange, onAttemptSaved, authorizationChoices, previewStep: suppliedPreviewStep, onPreviewStepChange: suppliedPreviewStepChange }:
  FormProps & { previewStep?: number; onPreviewStepChange?: (step: number) => void; authorizationChoices?: ReactNode; offer?: AuthorizationOffer; initialPackageAttempt?: PackageAttempt | null; initialFields?: PackageApplicationFields; onOfferRejected?: (fields: PackageApplicationFields) => void; onFieldsChange?: (fields: PackageApplicationFields) => void; onAttemptSaved?: (pending: PackageAttempt) => void }) {
  const preview = useContext(ApplicationPreview);
  const previewStep = suppliedPreviewStep ?? preview?.step;
  const onPreviewStepChange = suppliedPreviewStepChange ?? preview?.onChange;
  const formRef = useRef<HTMLFormElement>(null);
  const [demoStep, setDemoStep] = useState(1);
  const [currentStage, setCurrentStage] = useState(initialPackageAttempt ? 3 : 1);
  const stage = previewStep ?? currentStage;
  const setStage = (next: number) => { setCurrentStage(next); onPreviewStepChange?.(next); };
  const paymentStep = stage === 3;
  useEffect(() => {
    const form = formRef.current;
    const dialog = form?.closest("dialog");
    if (dialog) dialog.scrollTop = 0;
    form?.focus({ preventScroll: true });
  }, [stage]);
  const [scope, setScope] = useState<AuthorizationScope>(initialPackageAttempt?.fields.authorizationScope ?? prepareApplicationScope(initialFields?.authorizationScope ?? { version: 1, values: { materialId: `MIRA-${avatarId}`, other: "" } }));
  const [demoPackage, setDemoPackage] = useState(LICENSE_PACKAGES[0]);
  const [projectName, setProjectName] = useState(initialPackageAttempt?.fields.dramaName ?? initialFields?.dramaName ?? projectContext?.fields.dramaName ?? "");
  const browser = useSyncExternalStore(subscribeToHydration, getPaymentBrowser, serverPaymentBrowser);
  const [packageAttempt, setPackageAttempt] = useState(initialPackageAttempt);
  const [projectLegacyAttempt, setProjectLegacyAttempt] = useState<ProjectLegacyAttempt | null>(null);
  const [agreementBuyer, setAgreementBuyer] = useState<{ companyName: string; creditCode: string } | null>(null);
  const [buyerStatus, setBuyerStatus] = useState<"idle" | "loading" | "ready" | "missing" | "error">("idle");
  const [buyerRetry, setBuyerRetry] = useState(0);
  const [accepted, setAccepted] = useState(false);
  useEffect(() => {
    if (!paymentStep || demo || packageAttempt || projectLegacyAttempt) return;
    const controller = new AbortController();
    queueMicrotask(() => { if (!controller.signal.aborted) { setAgreementBuyer(null); setBuyerStatus("loading"); setAccepted(false); } });
    void (async () => {
      try {
        let response = await fetch("/api/workspace/data/agreement-buyer", { cache: "no-store", signal: controller.signal });
        let result = await response.json().catch(() => null);
        // During local development the deployed site may not yet have the new
        // narrow endpoint; the existing owner-only profile has the same checks.
        if (response.status === 404) {
          response = await fetch("/api/workspace/data/enterprise-profile", { cache: "no-store", signal: controller.signal });
          const previous = await response.json().catch(() => null);
          const profile = previous?.data?.profile;
          result = { data: profile?.status === "verified" ? { state: "available", companyName: profile.companyName, creditCode: profile.creditCode } : { state: "unavailable" } };
        }
        if (!response.ok) throw new Error("unavailable");
        const party = result?.data;
        if (party?.state !== "available") { if (!controller.signal.aborted) setBuyerStatus("missing"); return; }
        if (typeof party.companyName !== "string" || !party.companyName.trim() || typeof party.creditCode !== "string"
          || !/^[0-9A-Z]{18}$/u.test(party.creditCode)) throw new Error("incomplete");
        if (!controller.signal.aborted) { setAgreementBuyer({ companyName: party.companyName, creditCode: party.creditCode }); setBuyerStatus("ready"); }
      } catch {
        if (!controller.signal.aborted) setBuyerStatus("error");
      }
    })();
    return () => controller.abort();
  }, [paymentStep, buyerRetry, demo, packageAttempt, projectLegacyAttempt]);
  const [agreementChoices, setAgreementChoices] = useState<AgreementChoices>({});
  const agreementReader = useRef<PrivacyConsentHandle>(null);
  const agreementConsentId = useId();
  const useH5 = projectLegacyAttempt ? projectLegacyAttempt.channel === "h5" : packageAttempt ? packageAttempt.channel === "h5" : offer?.version !== 2 && h5Enabled && browser === "h5";
  const ready = useSyncExternalStore(subscribeToHydration, getClientSnapshot, getServerSnapshot);
  const attempt = useRef({ fingerprint: "", key: "" });
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ApplicationResult | null>(null);
  const [projectLegacyReady, setProjectLegacyReady] = useState(demo || Boolean(offer));
  useEffect(() => {
    if (offer || demo) return;
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      try {
        const raw = sessionStorage.getItem(legacyAuthorizationAttemptKey(userId, avatarId, projectContext));
        if (raw) {
          const restored = parseProjectLegacyAttempt(JSON.parse(raw), avatarId);
          if (!restored) throw new Error("原申请无法恢复，请先在授权中心核对记录。");
          setProjectLegacyAttempt(restored);
          setCurrentStage(3);
        }
        setProjectLegacyReady(true);
      } catch { setError("浏览器无法恢复原申请，请先在授权中心核对记录或检查存储权限。"); }
    });
    return () => { active = false; };
  }, [projectContext, offer, userId, avatarId, demo]);
  const [projectMessage, setProjectMessage] = useState("");
  const [existingOrderMessage, setExistingOrderMessage] = useState("此角色已关联真实订单，请继续查看原单。");
  const [existingOrderId, setExistingOrderId] = useState(projectContext?.authorizationId ?? null);
  const [h5Result, setH5Result] = useState<H5PaymentResult | null>(null);
  const frozenFields = packageAttempt?.fields ?? projectLegacyAttempt?.fields;
  const displayedScope = frozenFields?.authorizationScope ?? (frozenFields ? undefined : prepareApplicationScope({
    ...scope, values: { ...scope.values, purpose: projectName.trim() },
  }));
  const displayedBuyer = frozenFields?.agreementConsent ?? agreementBuyer ?? buyer;
  const canDisplayAgreement = !frozenFields || frozenFields.agreementConsent?.templateSha256 === agreement.sha256;
  const preserveDraft = (nextScope: AuthorizationScope, nextProjectName: string) => {
    if (!offer || frozenFields) return;
    onFieldsChange?.({ avatarId, offerHash: offer.quoteHash, dramaName: nextProjectName,
      roleName: initialFields?.roleName || projectContext?.fields.roleName || "未提供",
      roleTraits: initialFields?.roleTraits || projectContext?.fields.roleTraits || "未提供",
      pastWorks: initialFields?.pastWorks || projectContext?.fields.pastWorks || "未提供",
      authorizationScope: nextScope });
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (previewStep !== undefined) {
      if (previewStep === 1) setScope(prepareApplicationScope({ ...scope, values: { ...scope.values, purpose: String(new FormData(event.currentTarget).get("dramaName") ?? "").trim() } }));
      onPreviewStepChange?.(previewStep === 1 ? 3 : 4); return;
    }
    if (!ready || !projectLegacyReady || (offer?.version !== 2 && h5Enabled && browser === "wechat") || submittingRef.current) return;
    if (stage === 1) {
      const applicationScope = prepareApplicationScope({
        ...scope,
        values: { ...scope.values, purpose: String(new FormData(event.currentTarget).get("dramaName") ?? "").trim() },
      });
      const issue = authorizationScopeError(applicationScope);
      if (issue) {
        setError(issue.message);
        event.currentTarget.querySelector<HTMLElement>(`[data-scope-field="${issue.key}"] input, [data-scope-field="${issue.key}"] textarea`)?.focus();
        return;
      }
      setScope(parseAuthorizationScope(applicationScope)!); setAccepted(false); setError(""); setStage(3); return;
    }
    if (!accepted && !frozenFields) { setError("请阅读并同意《单项目人像授权协议》。"); agreementReader.current?.requestConsent(() => {}); return; }
    if (demo) { setError(""); setDemoStep(4); return; }
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    const form = new FormData(event.currentTarget);
    if (!frozenFields && (!agreementBuyer?.companyName || !agreementBuyer?.creditCode || buyerStatus !== "ready")) {
      setError("请先重新读取完整企业资料，再阅读并同意协议。"); submittingRef.current = false; setSubmitting(false); return;
    }
    const fields = packageAttempt?.fields ?? projectLegacyAttempt?.fields ?? { avatarId, agreementConsent: { accepted: true as const, templateSha256: agreement.sha256, companyName: agreementBuyer!.companyName, creditCode: agreementBuyer!.creditCode! }, authorizationScope: parseAuthorizationScope(scope)!, dramaName: String(form.get("dramaName") ?? ""), roleName: String(form.get("roleName") ?? ""),
      roleTraits: String(form.get("roleTraits") ?? ""), pastWorks: String(form.get("pastWorks") ?? ""), ...(offer ? { offerHash: offer.quoteHash, ...((offer.package.platformPackage?.id ?? offer.package.creatorPackage?.id) ? { packageId: offer.package.platformPackage?.id ?? offer.package.creatorPackage?.id } : {}) } : {}) };
    try {
      if (!frozenFields && fields.authorizationScope) {
        const supportResponse = await fetch("/api/v1/cloudbase-authorizations/offer", {
          method: "POST", cache: "no-store", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ avatarId, scopeOnly: true }),
        });
        const support = await supportResponse.json().catch(() => null);
        if (!supportResponse.ok || support?.authorizationScopeVersion !== 1 || support?.reviewWorkflowVersion !== 2) throw new Error("授权范围保存服务尚未就绪，请稍后再付款；填写内容已保留。");
      }
      if (offer) {
        const pending = packageAttempt ?? parsePackageAttempt({ version: 1, key: crypto.randomUUID(), channel: useH5 ? "h5" : "native", fields, offer }, avatarId);
        if (!pending) throw new Error("请完整填写申请内容。");
        const storageKey = packageAttemptKey(userId, avatarId, projectContext);
        const serialized = JSON.stringify(pending);
        // Persist before sending; losing an unknown request must never create a second payable order.
        try {
          sessionStorage.setItem(storageKey, serialized);
          if (sessionStorage.getItem(storageKey) !== serialized) throw new Error();
        } catch { throw new Error("浏览器无法保留原付款请求，请检查存储权限后重试。"); }
        setPackageAttempt(pending); onAttemptSaved?.(pending);
        attempt.current = { fingerprint: "package", key: pending.key };
      } else {
        const pending = projectLegacyAttempt ?? parseProjectLegacyAttempt({ key: crypto.randomUUID(), channel: useH5 ? "h5" : "native", fields }, avatarId);
        if (!pending) throw new Error("请完整填写申请内容。");
        const storageKey = legacyAuthorizationAttemptKey(userId, avatarId, projectContext);
        const serialized = JSON.stringify(pending);
        try {
          sessionStorage.setItem(storageKey, serialized);
          if (sessionStorage.getItem(storageKey) !== serialized) throw new Error();
        } catch { throw new Error("浏览器无法保留原付款申请，请检查存储权限后重试。"); }
        setProjectLegacyAttempt(pending);
        attempt.current = { fingerprint: "project", key: pending.key };
      }
      const response = await fetch(useH5 ? "/api/v1/cloudbase-authorizations/h5" : "/api/v1/cloudbase-authorizations/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...fields,
          idempotencyKey: attempt.current.key,
          ...(projectContext ? { projectContext: { projectId: projectContext.projectId, roleId: projectContext.roleId } } : {}),
        }),
      });
      const payload: unknown = await response.json().catch(() => null);
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        throw new Error("付款请求暂时未返回结果，请保留本页并继续核对原付款请求，勿重新申请。");
      }
      const body = payload as ApplicationResult & { message?: string; offerCode?: string; existingProjectOrderId?: string; checkoutPending?: boolean; projectLink?: { linked: boolean; message: string } };
      if (response.status === 409 && typeof body.existingProjectOrderId === "string" && /^[A-Za-z0-9_.:-]{3,256}$/u.test(body.existingProjectOrderId)) { setExistingOrderId(body.existingProjectOrderId); return; }
      if (response.status === 409 && body.offerCode === "PAYMENT_CLOSED") {
        attempt.current = { fingerprint: "", key: "" };
        try { sessionStorage.removeItem(offer ? packageAttemptKey(userId, avatarId, projectContext) : legacyAuthorizationAttemptKey(userId, avatarId, projectContext)); }
        catch { /* In-memory retry can still use a fresh key if browser storage is unavailable. */ }
        if (offer) { setPackageAttempt(null); setAccepted(false); onOfferRejected?.(fields as PackageApplicationFields); }
        else {
          setProjectName(fields.dramaName);
          if (fields.authorizationScope) setScope(fields.authorizationScope);
          setProjectLegacyAttempt(null); setStage(1); setAccepted(false);
          setError("原订单已核实关闭。请重新核对申请内容后再次提交。");
        }
        return;
      }
      if (!offer && response.status === 409 && body.offerCode === "AGREEMENT_CHANGED") {
        sessionStorage.removeItem(legacyAuthorizationAttemptKey(userId, avatarId, projectContext));
        setProjectName(fields.dramaName);
        if (fields.authorizationScope) setScope(fields.authorizationScope);
        setProjectLegacyAttempt(null); setStage(1); setAccepted(false); setBuyerRetry(value => value + 1);
        setError("企业资料或协议已更新，请重新阅读并同意本次协议。");
        return;
      }
      if (offer && response.status === 409 && ["PACKAGE_CHANGED", "PACKAGE_CONFIRMATION_REQUIRED", "PACKAGE_DISABLED", "PACKAGE_NOT_APPROVED", "PACKAGE_SELECTION_REQUIRED", "AGREEMENT_CHANGED"].includes(body.offerCode ?? "")) {
        sessionStorage.removeItem(packageAttemptKey(userId, avatarId, projectContext));
        setPackageAttempt(null); setAccepted(false);
        onOfferRejected?.(fields as PackageApplicationFields);
        return;
      }
      if (!response.ok) throw new Error(body.message || "授权申请创建失败，请稍后重试");
      if (projectContext && typeof body.projectLink?.message === "string") setProjectMessage(body.projectLink.message);
      if (body.checkoutPending === true && typeof body.authorizationId === "string" && /^[A-Za-z0-9_.:-]{3,256}$/u.test(body.authorizationId)) {
        setExistingOrderMessage("真实订单已创建，付款信息正在核对，请查看原订单，勿重复申请。");
        setExistingOrderId(body.authorizationId); return;
      }
      if (useH5) {
        if (!isH5PaymentResult(body)) throw new Error("付款结果待确认，请到授权中心查看原订单");
        rememberH5Payment(userId, body);
        setH5Result(body);
        return;
      }
      if (typeof body.authorizationId !== "string" || !/^[A-Za-z0-9_.:-]{3,256}$/u.test(body.authorizationId)
        || typeof body.amount !== "number" || !Number.isFinite(body.amount) || body.amount <= 0 || typeof body.paid !== "boolean"
        || (!body.paid && (typeof body.qrCodeDataUrl !== "string" || !body.qrCodeDataUrl.startsWith("data:image/png;base64,")))) {
        throw new Error("付款结果待确认，请保留原申请重试或到授权中心查看。");
      }
      setResult(body);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "授权申请创建失败，请稍后重试");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  if (previewStep === 4 || (demo && demoStep > 1)) return <div className="mt-5 grid gap-4 text-center">
    <ApplicationSteps step={previewStep ?? demoStep} />
    <h3 className="text-lg font-semibold">{previewStep !== undefined ? "待平台初审" : "模拟付款完成，等待平台初审"}</h3><Button type="button" variant="secondary" onClick={() => { setDemoStep(1); setStage(1); setAccepted(false); setAgreementChoices({}); }}>重新体验</Button>
  </div>;
  const projectNotice = projectContext ? <div className="mt-4 grid gap-2 text-sm"><p role="status">{projectMessage || `${projectContext.projectName} / ${projectContext.roleName}`}</p><ButtonLink href={`/partner/projects/${projectContext.projectId}#project-roles`} variant="secondary">返回项目查看进度</ButtonLink></div> : null;
  if (existingOrderId && previewStep === undefined) return <div className="mt-4 grid gap-3 text-sm"><p>{existingOrderMessage}</p><ButtonLink href={`/partner/authorizations?order=${encodeURIComponent(existingOrderId)}`}>查看角色原订单与付款进度</ButtonLink>{projectNotice}</div>;
  if (h5Result && previewStep === undefined) return <><ApplicationSteps step={3} /><H5PaymentPanel initial={h5Result} userId={userId} />{projectNotice}</>;

  if (result && previewStep === undefined) return <><ApplicationSteps step={3} /><NativePaymentPanel key={result.authorizationId} authorizationId={result.authorizationId}
    initialCheckout={{ authorizationId: result.authorizationId, amountMinor: Math.round(result.amount * 100),
      state: result.paid ? "paid" : "pending", qrCodeDataUrl: result.qrCodeDataUrl, downloadable: false }} />{projectNotice}</>;

  if (previewStep === undefined && offer?.version !== 2 && h5Enabled && browser === "wechat") return <p className="mt-5 text-sm leading-6">请在手机系统浏览器打开本页付款，或在小程序内提交申请。当前网页入口尚未支持微信内支付。</p>;

  return (
    <form noValidate={previewStep !== undefined} ref={formRef} tabIndex={-1} key={projectLegacyAttempt?.key ?? "draft"} onSubmit={submit} className="mt-5 grid gap-3">
      <ApplicationSteps step={stage} />

      {stage === 1 && projectContext && <p className="text-sm leading-6">本次申请用于「{projectContext.projectName} / {projectContext.roleName}」，创建后自动关联此角色。规划内容超出申请字数限制时，请在下方填写简明说明；项目原文保留。</p>}
      {stage === 1 && !offer && !demo && <p className="text-sm text-[var(--color-muted)]">按本次项目申请，默认授权期限 1 年，须由授权方确认。</p>}
      {(packageAttempt || projectLegacyAttempt) && <p role="status" className="text-sm leading-6">已保留原申请。请继续核对这笔付款，不要重复创建订单。<a href="/partner/authorizations" className="ml-2 underline">查看原订单</a></p>}
      <fieldset disabled={!ready || !projectLegacyReady || submitting} className="contents">
      <div className={stage !== 1 ? "hidden" : "contents"}>
      <div className="grid gap-3">
        <label className="grid gap-1.5 text-sm font-medium">
          申请项目名称
          <input name="dramaName" value={projectLegacyAttempt?.fields.dramaName ?? projectName} onChange={event => { setProjectName(event.target.value); setAccepted(false); preserveDraft(scope, event.target.value); }} readOnly={Boolean(packageAttempt || projectLegacyAttempt)} required maxLength={15} className="rounded-[var(--radius-input)] border border-[var(--color-line)] bg-[var(--color-sunken)] px-3 py-2.5 text-sm font-normal text-[var(--color-paper)]" />
        </label>
        <input type="hidden" name="roleName" value={initialPackageAttempt?.fields.roleName || projectLegacyAttempt?.fields.roleName || initialFields?.roleName || projectContext?.fields.roleName || "未提供"} />
      </div>
      {stage === 1 && (demo ? <fieldset className="grid gap-2"><legend className="mb-2 text-sm font-medium">授权用途</legend>{LICENSE_PACKAGES.map(item => <label key={item.id} className="flex items-center gap-3 rounded-xl border border-[var(--color-line)] p-3 text-sm"><input type="radio" name="demoPackage" checked={demoPackage.id === item.id} onChange={() => setDemoPackage(item)} /><span className="flex-1">{item.name} · {item.summary}</span><b>¥{((isInternalTestAvatarId(avatarId) ? 1 : item.priceFen) / 100).toFixed(2)}</b></label>)}</fieldset> : authorizationChoices)}
      <input type="hidden" name="roleTraits" value={initialPackageAttempt?.fields.roleTraits || projectLegacyAttempt?.fields.roleTraits || initialFields?.roleTraits || projectContext?.fields.roleTraits || "未提供"} />
      <input type="hidden" name="pastWorks" value={initialPackageAttempt?.fields.pastWorks || projectLegacyAttempt?.fields.pastWorks || initialFields?.pastWorks || projectContext?.fields.pastWorks || "未提供"} />
      </div>
      {stage === 1 && <fieldset disabled={Boolean(frozenFields)} className="contents"><AuthorizationScopeFields scope={frozenFields?.authorizationScope ?? scope} onChange={next => { setScope(next); setAccepted(false); preserveDraft(next, projectName); }} /></fieldset>}
      {paymentStep && canDisplayAgreement && <>
      {!frozenFields && buyerStatus === "loading" && <p role="status" className="text-xs text-[var(--color-muted)]">正在读取已认证企业资料…</p>}
      {!frozenFields && (buyerStatus === "missing" || buyerStatus === "error") && <p role="status" className="text-xs text-[var(--color-muted)]">{buyerStatus === "missing" ? "完整企业资料暂不可用，请由企业负责人核对认证资料。" : "企业资料暂时无法读取，请稍后重试。"} <button type="button" className="underline" onClick={() => setBuyerRetry(value => value + 1)}>重新读取</button></p>}
      <div id={agreementConsentId}>
        <ScrollConsent disabled={!demo && !frozenFields && buyerStatus !== "ready"} readOnly={Boolean(frozenFields)} ref={agreementReader} agreed={frozenFields ? true : accepted} onChange={setAccepted} title={agreement.title}>
          <ProjectAgreementChoices amountMinor={offer?.amountMinor ?? (demo ? (isInternalTestAvatarId(avatarId) ? 1 : demoPackage.priceFen) : undefined)} usage={offer?.package.name ?? (demo ? demoPackage.name : undefined)} term={demo ? `确认起 ${demoPackage.months}个月` : agreementTerm(offer)} buyer={displayedBuyer} scope={displayedScope} choices={agreementChoices} onChange={setAgreementChoices} />
        </ScrollConsent>
      </div>
      </>}
      {error && <p role="alert" className="text-xs leading-5 text-[var(--color-pending)]">{error}</p>}
      <GuidedSubmitButton type="submit" className="btn-silver inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-pill)] px-6 py-3 text-[15px] font-medium disabled:opacity-50" disabled={!ready || !projectLegacyReady || submitting} reason={previewStep === undefined && paymentStep && !frozenFields && !accepted ? "请阅读并同意《单项目人像授权协议》。" : ""} onBlocked={() => agreementReader.current?.requestConsent(() => {})} focusSelector={`[id="${agreementConsentId}"] input[type="checkbox"]`}>
        {submitting ? "正在核对付款请求…" : previewStep !== undefined ? "下一步" : !paymentStep ? "下一步" : demo ? "模拟付款完成" : (packageAttempt || projectLegacyAttempt) ? "继续核对原付款请求" : "确认并付款"}
      </GuidedSubmitButton>
      {stage > 1 && !packageAttempt && !projectLegacyAttempt && <Button type="button" variant="secondary" disabled={submitting} onClick={() => { setStage(1); setAccepted(false); setAgreementChoices({}); setError(""); }}>上一步</Button>}
      </fieldset>
    </form>
  );
}
